/**
 * Hook per aprire il Customer Portal Lemon Squeezy.
 *
 * Chiama l'edge function `lemonsqueezy-customer-portal` per recuperare i
 * signed URL (a scadenza, non si possono cacheare), poi apre la pagina in
 * nuovo tab.
 *
 * Due varianti:
 *  - openCustomerPortal() → portale completo (cambia carta, annulla, fatture)
 *  - openUpdatePayment()  → shortcut solo metodo di pagamento
 *
 * Status: 'idle' | 'loading'. Niente success — al success facciamo direttamente
 * window.open(url, '_blank').
 */
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import { Sentry } from '@/lib/sentry';

type PortalVariant = 'portal' | 'payment';

interface UseCustomerPortalResult {
  openCustomerPortal: () => Promise<void>;
  openUpdatePayment: () => Promise<void>;
  isLoading: boolean;
}

export function useCustomerPortal(): UseCustomerPortalResult {
  const { currentOrganizationId } = useCurrentOrganization();
  const [isLoading, setIsLoading] = useState(false);

  const fetchUrl = async (variant: PortalVariant): Promise<string | null> => {
    if (isLoading) return null;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'lemonsqueezy-customer-portal',
        { body: { organizationId: currentOrganizationId } },
      );

      if (error) {
        throw new Error(error.message || 'Errore chiamata edge function');
      }

      const payload = data as {
        customerPortalUrl?: string | null;
        updatePaymentUrl?: string | null;
        error?: string;
      };
      if (payload.error) throw new Error(payload.error);

      const url = variant === 'payment'
        ? (payload.updatePaymentUrl ?? payload.customerPortalUrl)
        : (payload.customerPortalUrl ?? payload.updatePaymentUrl);

      if (!url) {
        throw new Error('Lemon Squeezy non ha restituito un URL valido');
      }
      return url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Errore sconosciuto';
      toast.error('Impossibile aprire il portale', { description: msg });
      Sentry.captureException(e, {
        tags: { feature: 'lemonsqueezy-customer-portal', variant },
        extra: { organizationId: currentOrganizationId },
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    const url = await fetchUrl('portal');
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openUpdatePayment = async () => {
    const url = await fetchUrl('payment');
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return { openCustomerPortal, openUpdatePayment, isLoading };
}
